import { z } from 'zod';

const assetSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(['environment', 'sprite', 'audio']),
  src: z.string().startsWith('/'),
  fallback: z.string().min(1),
  critical: z.boolean(),
});

export type AssetDefinition = z.infer<typeof assetSchema>;

export const ASSET_MANIFEST = {
  wreckBackdrop: {
    id: 'wreck-backdrop',
    kind: 'environment',
    src: '/art/wreck-cutaway-master.png',
    fallback:
      'radial-gradient(circle at 50% 78%, #0e3850 0, #06131d 25%, transparent 52%), linear-gradient(180deg, #02070c, #06131a)',
    critical: false,
  },
  salvageDrone: {
    id: 'salvage-drone',
    kind: 'sprite',
    src: '/art/salvage-drone.png',
    fallback:
      'radial-gradient(circle at 58% 50%, #4fd8ff 0 6%, #d8e8eb 7% 24%, #132732 25% 62%, transparent 64%)',
    critical: false,
  },
  explorationMusic: {
    id: 'music-exploration',
    kind: 'audio',
    src: '/audio/exploration.mp3',
    fallback: 'Game remains playable with music muted',
    critical: false,
  },
  tensionMusic: {
    id: 'music-tension',
    kind: 'audio',
    src: '/audio/tension.mp3',
    fallback: 'Exploration music remains active at elevated instability',
    critical: false,
  },
  emergencyMusic: {
    id: 'music-emergency',
    kind: 'audio',
    src: '/audio/emergency.mp3',
    fallback: 'Warning effects and emergency HUD remain active',
    critical: false,
  },
} as const satisfies Record<string, AssetDefinition>;

export function validateAssetManifest(
  manifest: Readonly<Record<string, unknown>> = ASSET_MANIFEST,
): AssetDefinition[] {
  const parsed = Object.values(manifest).map((asset) => assetSchema.parse(asset));
  const ids = new Set<string>();
  for (const asset of parsed) {
    if (ids.has(asset.id)) throw new Error(`Duplicate asset id: ${asset.id}`);
    ids.add(asset.id);
  }
  return parsed;
}
